import { Input, Spinner } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { redirectTarget } from '@/lib/auth/redirectTarget';
import { confirmTwoFactor, startTwoFactorEnrolment } from '@/lib/auth/api';
import { isSessionStatus } from '@/lib/auth/api';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * Mandatory enrolment, for a Super Admin who has never set a second factor up.
 *
 * docs/08-INFRA-DEVOPS.md §5.2 makes 2FA mandatory for that role, and §5 of the
 * API spec says login must refuse them a full token without it. They cannot be
 * left locked out either, so login hands back an enrolment token that reaches
 * this screen and nothing else.
 *
 * Confirming from an enrolment token returns a full token in the same response,
 * so this is also where the session begins — otherwise they would have to sign
 * in twice.
 */
interface SetupFields {
  code: string;
}

export function TwoFactorSetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, adoptSession } = useAuth();

  const [formError, setFormError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  /*
   * Enrolment is started by a POST that generates a secret, so it must happen
   * once and not on every render. `staleTime: Infinity` and no retry: asking
   * twice would replace the secret the person is part-way through scanning.
   */
  const enrolment = useQuery({
    queryKey: ['auth', 'two-factor', 'enrolment'],
    queryFn: startTwoFactorEnrolment,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: session?.status === 'two_factor_setup_required',
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SetupFields>({ defaultValues: { code: '' } });

  if (session?.status !== 'two_factor_setup_required') {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      const result = await confirmTwoFactor(values.code);

      if (result.token && isSessionStatus(result.status)) {
        adoptSession({
          token: result.token,
          token_type: 'Bearer',
          expires_at: result.expires_at ?? null,
          status: result.status,
        });

        navigate(redirectTarget(location), { replace: true });

        return;
      }

      // Confirmed but no token — the enrolment token was already a full one,
      // which cannot happen on this screen. Start again rather than guess.
      navigate('/login', { replace: true });
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, ['code']);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (enrolment.isPending) {
    return (
      <div className="grid place-items-center py-8">
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  if (enrolment.isError || !enrolment.data) {
    return <FormError message={t('errors.generic')} />;
  }

  const { qr_code_svg: qr, secret, recovery_codes: recoveryCodes } = enrolment.data.data;

  return (
    <div className="space-y-6">
      <ol className="space-y-4 text-body-sm text-secondary">
        <li>{t('auth.setup.step1')}</li>
        <li>
          {t('auth.setup.step2')}

          <div
            /*
             * Fortify renders the QR itself, so this is server-built SVG rather
             * than markup from a person — the one place in the dashboard where
             * `dangerouslySetInnerHTML` is not a sanitiser question.
             */
            dangerouslySetInnerHTML={{ __html: qr }}
            className="mt-3 w-fit rounded-card bg-white p-4 [&_svg]:h-44 [&_svg]:w-44"
          />

          <button
            type="button"
            onClick={() => setShowSecret((current) => !current)}
            className="mt-2 text-body-sm text-link underline-offset-2 hover:underline"
          >
            {showSecret ? t('auth.setup.hideSecret') : t('auth.setup.showSecret')}
          </button>

          {showSecret ? (
            <code className="mt-2 block break-all rounded-card bg-surface-alt p-3 font-mono text-body-sm text-primary">
              {secret}
            </code>
          ) : null}
        </li>
      </ol>

      {/* Shown before the code field, not after: once this screen is left they
          are not shown again, and a person who has already typed their code is
          past reading. */}
      <section className="rounded-card border border-subtle p-4">
        <h3 className="text-body font-extrabold text-primary">{t('auth.setup.recoveryTitle')}</h3>
        <p className="mt-1 text-body-sm text-secondary">{t('auth.setup.recoveryBody')}</p>

        <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-body-sm text-primary">
          {recoveryCodes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
      </section>

      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <Input
          {...register('code', { required: t('auth.validation.codeRequired') })}
          inputMode="numeric"
          autoComplete="one-time-code"
          label={t('auth.fields.code')}
          hint={t('auth.setup.confirmHint')}
          error={errors.code?.message}
          required
        />

        <SubmitButton fullWidth loading={isSubmitting}>
          {t('auth.setup.submit')}
        </SubmitButton>
      </form>
    </div>
  );
}
